/**
 * useModerationSubmit hook
 *
 * Encapsulates batch feedback submission logic for the moderation work queue.
 * Builds feedback items from session state and handles Submit / Submit-and-Stop actions.
 */

import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useRef } from 'react';

import type { CaseFeedbackInput } from '../domain/CaseFeedback';
import type { ModerationCase } from '../domain/ModerationCase';
import type { CaseCardState } from '../domain/ModerationSession';

import { useSubmitBatchCaseFeedback } from './useSubmitBatchCaseFeedback';

interface UseModerationSubmitParams {
  /** Case IDs that have labels applied and are ready to submit */
  appliedCaseIds: string[];
  /** All session cases currently displayed */
  sessionCases: ModerationCase[];
  /** Per-card state map (keyed by caseId) */
  cardStates: Record<string, CaseCardState>;
  /** Dispatches SUBMIT_CASES to remove submitted cards from session */
  submitCases: (submittedCaseIds: string[]) => void;
  /** Dispatches STOP_SESSION to mark session inactive (disables nav blocker) */
  stopSession: () => void;
  /** Community ID for re-fetching next batch */
  communityId: string;
  /** Topic name for re-fetching next batch */
  topicName: string;
  /** Lazy query to fetch the next batch of cases */
  assignCases: (options: { variables: { communityId: string; topicName: string; filterId?: string } }) => void;
  /** Optional ABR filter ID for filtered case assignment */
  filterId?: string;
  /** Callback to navigate back to dashboard */
  onReturnToDashboard: () => void;
}

export interface UseModerationSubmitReturn {
  /** Submit all applied cases, then load next batch */
  handleSubmit: () => Promise<void>;
  /** Submit all applied cases, then navigate to dashboard */
  handleSubmitAndStop: () => Promise<void>;
  /** Whether batch submission is in progress */
  submitLoading: boolean;
  /** Submission errors */
  submitErrors: DomainError[] | null;
  /** Ref tracking whether initial cases have been loaded into session */
  casesLoadedRef: React.MutableRefObject<boolean>;
}

/**
 * Hook that manages batch feedback submission for the moderation queue.
 *
 * @returns Submit handlers, loading state, errors, and casesLoadedRef
 */
export function useModerationSubmit({
  appliedCaseIds,
  sessionCases,
  cardStates,
  submitCases,
  stopSession,
  communityId,
  topicName,
  assignCases,
  filterId,
  onReturnToDashboard,
}: UseModerationSubmitParams): UseModerationSubmitReturn {
  const { submitBatchFeedback, loading: submitLoading, errors: submitErrors } = useSubmitBatchCaseFeedback();
  const casesLoadedRef = useRef(false);

  /** Build batch feedback items from all applied cards in the session */
  const buildBatchFeedbackItems = useCallback(
    (): CaseFeedbackInput[] =>
      appliedCaseIds
        .map(caseId => {
          const moderationCase = sessionCases.find(c => c.caseId === caseId);
          const cardState = cardStates[caseId];

          if (!moderationCase || !cardState) return null;

          return {
            communityId: moderationCase.communityId,
            topicName: moderationCase.topicName,
            subjectId: moderationCase.subjectId,
            caseId: moderationCase.caseId,
            feedbackLabels: cardState.appliedLabels,
            feedbackComment: cardState.comment,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null),
    [appliedCaseIds, sessionCases, cardStates]
  );

  /**
   * Handle Submit: submit all applied cases, then load next batch (T040).
   * Dispatches SUBMIT_CASES to remove submitted cards from session,
   * then calls assignModerationCase to fetch the next batch.
   */
  const handleSubmit = useCallback(async () => {
    const feedbackItems = buildBatchFeedbackItems();

    if (feedbackItems.length === 0) return;

    const result = await submitBatchFeedback(feedbackItems);

    if (result?.success) {
      submitCases(appliedCaseIds);

      track('moderation_batch_submit', {
        submittedCount: String(appliedCaseIds.length),
        action: 'submit_next',
      });

      // Reset load ref so next batch can be loaded
      casesLoadedRef.current = false;

      // Request next batch of cases (T040/T042)
      if (communityId && topicName) {
        void assignCases({ variables: { communityId, topicName, filterId } });
      }
    }
  }, [buildBatchFeedbackItems, submitBatchFeedback, submitCases, appliedCaseIds, communityId, topicName, assignCases, filterId]);

  /**
   * Handle Submit and Stop: submit all applied cases, then navigate to dashboard (T041).
   * On failure, stays on the page so the user can see the error and retry.
   */
  const handleSubmitAndStop = useCallback(async () => {
    const feedbackItems = buildBatchFeedbackItems();

    if (feedbackItems.length === 0) {
      stopSession();
      onReturnToDashboard();

      return;
    }

    const result = await submitBatchFeedback(feedbackItems);

    if (result?.success) {
      submitCases(appliedCaseIds);
      track('moderation_batch_submit', {
        submittedCount: String(feedbackItems.length),
        action: 'submit_and_stop',
      });

      // Only stop session and navigate on success to prevent data loss
      stopSession();
      onReturnToDashboard();
    }
    // On failure, submitErrors is already populated by useSubmitBatchCaseFeedback.
    // Stay on the page so the user can see the error banner and retry.
  }, [buildBatchFeedbackItems, submitBatchFeedback, submitCases, appliedCaseIds, stopSession, onReturnToDashboard]);

  return {
    handleSubmit,
    handleSubmitAndStop,
    submitLoading,
    submitErrors,
    casesLoadedRef,
  };
}
